/**
 * Shared pieces for the Administration pages (staff, roles, approvals, API credentials,
 * audit log): filter controls bound to the URL, the list shell that handles
 * loading / error / empty / paging, and the references that show a record by name.
 */

import { RefreshCw, RotateCcw } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTable, Field, Pager, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import type { Crumb } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import type { AsyncState } from '../../lib/useApi';
import { cn } from '@/lib/utils';

/** Breadcrumb trail for every page in this area. */
export function useAdminCrumbs(current?: string): Crumb[] {
  const { t } = useTranslation('admin');
  const trail: Crumb[] = [{ label: t('area') }];
  if (current) trail.push({ label: current });
  return trail;
}

/** Filters live in the URL, so a screen can be shared or reloaded unchanged. */
export function useParamSetter(): [URLSearchParams, (key: string, value: string) => void, () => void] {
  const [params, setParams] = useSearchParams();
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // Any change to what is listed invalidates the page cursor; opening a row does not.
    if (key !== 'before' && key !== 'request') next.delete('before');
    setParams(next);
  };
  return [params, set, () => setParams(new URLSearchParams())];
}

/**
 * Roles and permissions are reference data seeded in English. These give the operator-facing
 * name in the reader's own language, falling back to the description the server sent for a code
 * the console does not know yet. i18next splits keys on dots, so a code is keyed with `_`.
 */
export function useCatalogue(): {
  roleName: (code: string, fallback?: string | null) => string;
  permissionName: (code: string, fallback?: string | null) => string;
} {
  const { t } = useTranslation('admin');
  const key = (code: string) => code.replace(/\./g, '_');
  return {
    roleName: (code, fallback) => t(`roleName.${key(code)}` as 'roleName.administrator', { defaultValue: fallback || code }),
    permissionName: (code, fallback) => t(`permission.${key(code)}` as 'permission.domain_read', { defaultValue: fallback || code }),
  };
}

const ANY = '__any__';

export interface Option {
  value: string;
  label: string;
}

export function FilterSelect({
  label,
  value,
  onChange,
  options,
  className,
  anyLabel,
}: {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  className?: string;
  /** What "no filter" means on this screen, when it is not simply "any". */
  anyLabel?: string;
}) {
  const { t } = useTranslation('admin');
  return (
    <Field label={label} className={cn('min-w-36', className)}>
      <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? '' : v)}>
        <SelectTrigger size="sm" className="w-full min-w-36">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>{anyLabel ?? t('filter.any')}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function TextFilter({
  label,
  value,
  placeholder,
  onApply,
  className,
}: {
  label: ReactNode;
  value: string;
  placeholder?: string;
  onApply: (value: string) => void;
  className?: string;
}) {
  return (
    <Field label={label} className={cn('min-w-44', className)}>
      <Input
        key={value}
        type="search"
        className="h-8"
        defaultValue={value}
        placeholder={placeholder}
        spellCheck={false}
        onKeyDown={(e) => e.key === 'Enter' && onApply(e.currentTarget.value.trim())}
        onBlur={(e) => e.currentTarget.value.trim() !== value && onApply(e.currentTarget.value.trim())}
      />
    </Field>
  );
}

export function RefreshButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('admin');
  return (
    <Button type="button" variant="outline" size="sm" onClick={onClick}>
      <RefreshCw /> {t('filter.refresh')}
    </Button>
  );
}

export function ResetButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  const { t } = useTranslation('admin');
  return (
    <Button type="button" variant="ghost" size="sm" onClick={onClick} disabled={disabled}>
      <RotateCcw /> {t('filter.reset')}
    </Button>
  );
}

/**
 * A table in a card that handles every state of one read: loading, failure, nothing
 * found, rows, and the next page.
 */
export function ListSection<T>({
  title,
  description,
  result,
  rows,
  columns,
  rowKey,
  onRowClick,
  errorTitle,
  empty,
  nextBefore,
  onNext,
  onFirst,
  first,
  actions,
}: {
  title?: ReactNode;
  description?: ReactNode;
  result: AsyncState<unknown> & { reload: () => void };
  rows: readonly T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  errorTitle: string;
  empty: string;
  nextBefore?: string | null;
  onNext?: (cursor: string) => void;
  onFirst?: () => void;
  first?: boolean;
  actions?: ReactNode;
}) {
  return (
    <Section flush title={title} description={description} actions={actions}>
      {result.status === 'loading' ? (
        <div className="px-5">
          <Loading rows={6} />
        </div>
      ) : null}
      {result.status === 'error' ? (
        <div className="px-5 pb-5">
          <DataError error={result.error} onRetry={result.reload} title={errorTitle} />
        </div>
      ) : null}
      {result.status === 'success' && rows.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyState title={empty} />
        </div>
      ) : null}
      {result.status === 'success' && rows.length > 0 ? (
        <>
          <DataTable columns={columns} rows={rows} rowKey={rowKey} onRowClick={onRowClick} />
          {onNext && (nextBefore || !first) ? (
            <Pager canPrevious={!first} canNext={Boolean(nextBefore)} onPrevious={onFirst} onNext={() => nextBefore && onNext(nextBefore)} />
          ) : null}
        </>
      ) : null}
    </Section>
  );
}

/** A person, by name, linking to their staff page. Automated actors have a name of their own. */
export function PersonRef({ id, name, email, automatedLabel }: { id: string; name: string | null; email?: string | null; automatedLabel: string }) {
  if (!name) {
    // An automated actor carries its component name in the identifier itself.
    const looksLikeUuid = /^[0-9a-f-]{32,36}$/i.test(id);
    return looksLikeUuid ? (
      <span className="text-muted-foreground" title={id}>
        {automatedLabel}
      </span>
    ) : (
      <span>
        <span className="font-medium">{id}</span>
        <span className="block text-xs text-muted-foreground">{automatedLabel}</span>
      </span>
    );
  }
  return <EntityRef label={name} sub={email ?? undefined} to={`/admin/staff/${id}`} id={id} />;
}

/**
 * Where a record of a given kind lives in the console, when it has a page of its own.
 *
 * Most detail pages are keyed by the record's own id, which is what an audit entry or an
 * approval carries. A domain page is keyed by the *name*, so that link needs the resolved
 * name and there is no link without one — a link to `/domains/<uuid>` only leads to
 * "not found".
 */
export function recordPath(resourceType: string, resourceId: string, resourceName: string | null): string | undefined {
  const id = encodeURIComponent(resourceId);
  switch (resourceType) {
    case 'domain':
      return resourceName ? `/domains/${encodeURIComponent(resourceName)}` : undefined;
    case 'user':
      return `/admin/staff/${id}`;
    case 'customer':
      return `/customers/${id}`;
    case 'subject':
      return `/subjects/${id}`;
    case 'dossier':
      return `/dossiers/${id}`;
    case 'document':
      return `/documents/${id}`;
    case 'abuse_case':
      return `/compliance/icann/abuse/${id}`;
    case 'domain_dispute':
      return `/compliance/icann/disputes/${id}`;
    case 'disclosure_case':
      return `/compliance/icann/disclosure/${id}`;
    case 'registry_transaction':
      return `/registry/transactions/${id}`;
    case 'module_instance': {
      // `branch/module/instance` (ADR-025): the connection's page in Cài đặt › Module.
      const [branch, module, instance] = resourceId.split('/');
      return branch && module && instance ? `/settings?${new URLSearchParams({ tab: 'modules', branch, module, instance }).toString()}` : undefined;
    }
    case 'addon':
      // The add-on's id (ADR-025 §4.6): its card in Cài đặt › Module › Tiện ích.
      return `/settings?${new URLSearchParams({ tab: 'modules', branch: 'addons', addon: resourceId }).toString()}`;
    default:
      return undefined;
  }
}

/**
 * The record an entry refers to, by name when it has one. Records with no name of
 * their own (a wallet, a registry command) keep their short identifier — there is no
 * name to show instead.
 */
export function RecordRef({ resourceType, resourceId, resourceName }: { resourceType: string; resourceId: string | null; resourceName: string | null }) {
  const { t } = useTranslation('admin');
  if (!resourceId) return <span className="text-muted-foreground">—</span>;
  const kind = t(`resourceType.${resourceType}` as 'resourceType.domain', { defaultValue: resourceType });
  return <EntityRef label={resourceName ?? undefined} sub={kind} to={recordPath(resourceType, resourceId, resourceName)} id={resourceId} />;
}
